import { Controller } from "@hotwired/stimulus"

// EVA-229: idle-session UX. Warns the user with a countdown before the server
// signs them out for inactivity, and keeps the server session alive on genuine
// activity (so "staring at a table" with occasional mouse movement doesn't get
// you kicked out). The server (Devise :timeoutable + the absolute cap) remains
// the source of truth; this is a UX layer on top of it.
//
// Mounted once per authenticated layout with data values supplied by
// SessionTimeoutHelper. Reused for both the :user and :admin scopes.
export default class extends Controller {
  static values = {
    idleTimeout: Number,        // seconds of inactivity before the server logs out
    warningLead: { type: Number, default: 60 },      // seconds before expiry to warn
    heartbeatInterval: { type: Number, default: 300 }, // min seconds between activity pings
    heartbeatUrl: String,
    signOutUrl: String,
    signInUrl: String,
    warningTitle: String,
    warningBody: String,
    stay: String,
    signOutLabel: String,
    storageKey: { type: String, default: "eva:idle:lastActivity" }, // shared across tabs of the same scope
  }

  ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"]

  connect() {
    this.expired = false
    this.warningVisible = false
    this.lastActivity = Date.now()
    this.lastHeartbeat = Date.now()
    this.touch() // seed the cross-tab clock
    // Warning lead can't exceed half the window (e.g. very short idle timeouts).
    this.lead = Math.min(this.warningLeadValue, Math.floor(this.idleTimeoutValue / 2))

    this.onActivity = this.throttle(this.recordActivity.bind(this), 1000)
    this.ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, this.onActivity, { passive: true })
    )

    this.buildModal()
    this.ticker = setInterval(() => this.tick(), 1000)
  }

  disconnect() {
    this.teardown()
  }

  // --- activity tracking ---

  recordActivity() {
    if (this.expired || this.warningVisible) return // during the warning, require an explicit click
    this.touch()
    if (Date.now() - this.lastHeartbeat >= this.heartbeatIntervalValue * 1000) {
      this.sendHeartbeat()
    }
  }

  tick() {
    if (this.expired) return
    // Use the most recent activity across all tabs of this scope, so an idle
    // background tab doesn't log out a user who is active in another tab.
    const idleMs = Date.now() - this.lastActivityAcrossTabs()
    const remainingMs = this.idleTimeoutValue * 1000 - idleMs

    if (remainingMs <= 0) {
      this.expire()
    } else if (remainingMs <= this.lead * 1000) {
      this.showWarning(remainingMs)
    } else if (this.warningVisible) {
      this.hideWarning()
    }
  }

  // --- server keep-alive ---

  async sendHeartbeat() {
    this.lastHeartbeat = Date.now()
    try {
      const response = await fetch(this.heartbeatUrlValue, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
      // A redirect or non-2xx means the server already ended the session
      // (e.g. the absolute cap was hit) — stop pretending we're alive.
      if (!response.ok || response.redirected) this.expire()
    } catch (_e) {
      // Network hiccup — ignore; the next tick/heartbeat will retry.
    }
  }

  // --- warning modal ---

  staySignedIn() {
    this.touch()
    this.hideWarning()
    this.sendHeartbeat()
  }

  showWarning(remainingMs) {
    if (!this.warningVisible) {
      this.warningVisible = true
      this.overlay.style.display = "flex"
    }
    const secs = Math.max(0, Math.ceil(remainingMs / 1000))
    const mm = Math.floor(secs / 60)
    const ss = String(secs % 60).padStart(2, "0")
    this.countdownEl.textContent = `${mm}:${ss}`
  }

  hideWarning() {
    if (!this.warningVisible) return
    this.warningVisible = false
    this.overlay.style.display = "none"
  }

  async expire() {
    if (this.expired) return
    this.expired = true
    this.teardown()
    try {
      await fetch(this.signOutUrlValue, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this.csrfToken, Accept: "text/html" },
      })
    } catch (_e) {
      // ignore — we hard-redirect regardless
    }
    window.location.href = this.signInUrlValue
  }

  // --- helpers ---

  buildModal() {
    const overlay = document.createElement("div")
    overlay.className = "idle-timeout-overlay"
    Object.assign(overlay.style, {
      display: "none",
      position: "fixed",
      inset: "0",
      zIndex: "2000",
      background: "rgba(0,0,0,0.5)",
      alignItems: "center",
      justifyContent: "center",
    })

    const card = document.createElement("div")
    card.className = "card shadow"
    card.setAttribute("role", "alertdialog")
    card.setAttribute("aria-modal", "true")
    Object.assign(card.style, { maxWidth: "420px", width: "90%" })

    const body = document.createElement("div")
    body.className = "card-body text-center p-4"

    const title = document.createElement("h5")
    title.className = "card-title mb-3"
    title.textContent = this.warningTitleValue

    const text = document.createElement("p")
    text.className = "text-muted mb-2"
    text.textContent = this.warningBodyValue

    this.countdownEl = document.createElement("p")
    this.countdownEl.className = "display-6 fw-bold mb-4"
    this.countdownEl.textContent = ""

    const actions = document.createElement("div")
    actions.className = "d-flex gap-2 justify-content-center"

    const stayBtn = document.createElement("button")
    stayBtn.type = "button"
    stayBtn.className = "btn btn-primary"
    stayBtn.textContent = this.stayValue
    stayBtn.addEventListener("click", () => this.staySignedIn())

    const outBtn = document.createElement("button")
    outBtn.type = "button"
    outBtn.className = "btn btn-outline-secondary"
    outBtn.textContent = this.signOutLabelValue
    outBtn.addEventListener("click", () => this.expire())

    actions.append(stayBtn, outBtn)
    body.append(title, text, this.countdownEl, actions)
    card.append(body)
    overlay.append(card)
    // Append to the controller element (which is data-turbo-permanent) so the
    // overlay travels across Turbo navigations instead of being orphaned with
    // the discarded <body>. position:fixed still covers the viewport.
    this.element.append(overlay)
    this.overlay = overlay
  }

  teardown() {
    if (this.ticker) clearInterval(this.ticker)
    if (this.onActivity) {
      this.ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, this.onActivity))
    }
    if (this.overlay) this.overlay.remove()
  }

  // Record activity locally and broadcast it to other tabs of this scope.
  touch() {
    this.lastActivity = Date.now()
    try {
      localStorage.setItem(this.storageKeyValue, String(this.lastActivity))
    } catch (_e) {
      // localStorage unavailable (private mode / disabled) — fall back to local-only.
    }
  }

  lastActivityAcrossTabs() {
    let shared = 0
    try {
      shared = Number(localStorage.getItem(this.storageKeyValue)) || 0
    } catch (_e) {
      shared = 0
    }
    return Math.max(this.lastActivity, shared)
  }

  throttle(fn, wait) {
    let last = 0
    return (...args) => {
      const now = Date.now()
      if (now - last >= wait) {
        last = now
        fn(...args)
      }
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
